import { AlertTriangle, Check, X } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'v5/design-system/Badge';
import { Card } from 'v5/design-system/Card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from 'v5/design-system/Collapsible';
import { cn } from 'v5/platform/dom/cn';
import {
  Booking,
  VisitorVendorState
} from '../../../../api/visitor-vendor-state/visitor-vendor-state.api';
import { getVendorStatus } from '../../vendor-state/utils';
import { VendorIntegrationDetails } from './VendorIntegrationDetails';

interface VendorIntegrationCardProps {
  vendorId: string;
  vendorState: VisitorVendorState;
  booking: Booking;
}

/**
 * Card component for displaying vendor integration status and details.
 * Uses expandable sections instead of popovers for better UX.
 */
export const VendorIntegrationCard: React.FC<VendorIntegrationCardProps> = ({
  vendorId,
  vendorState,
  booking
}) => {
  const { t } = useTranslation('visitor-management');
  const [isExpanded, setIsExpanded] = useState(false);

  const statusInfo = getVendorStatus(vendorState);

  const getBadge = () => {
    switch (statusInfo.type) {
      case 'preparation-error':
        return (
          <Badge variant="danger" tone="solid" size="sm" className="gap-1">
            <X className="w-3 h-3" />
            <span>Error</span>
          </Badge>
        );
      case 'not-eligible':
        return (
          <Badge variant="gray" tone="solid" size="sm" className="gap-1">
            <span>Not Eligible</span>
          </Badge>
        );
      case 'drift':
        return (
          <Badge variant="attention" tone="solid" size="sm" className="gap-1">
            <AlertTriangle className="w-3 h-3" />
            <span>Drift</span>
          </Badge>
        );
      case 'synced':
      case 'registered':
        return (
          <Badge variant="success" tone="solid" size="sm" className="gap-1">
            <Check className="w-3 h-3" />
            <span>Synced</span>
          </Badge>
        );
      default:
        return (
          <Badge variant="gray" tone="solid" size="sm">
            <span>Unknown</span>
          </Badge>
        );
    }
  };
  const capitalizedVendor =
    vendorId.charAt(0).toUpperCase() + vendorId.slice(1);

  return (
    <Card className={cn('p-0 overflow-hidden')}>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger className="px-4 gap-4">
          <h4 className="font-semibold text-gray-900">{capitalizedVendor}</h4>
          <div className="hover:no-underline shrink-0">{getBadge()}</div>

          <div className="ml-auto text-right min-w-10">
            <div className="text-sm text-gray-600 truncate">
              {statusInfo.summary}
            </div>
            {vendorState.last_successful_at && (
              <div className="text-xs text-gray-500">
                Last sync:{' '}
                {new Date(vendorState.last_successful_at).toLocaleDateString()}
              </div>
            )}
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 border-t border-gray-100">
            <VendorIntegrationDetails
              vendorState={vendorState}
              vendorId={vendorId}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
